//styles
import 'react-circular-progressbar/dist/styles.css';

import React, { useCallback } from 'react';
import isURL from 'validator/lib/isURL';

import { canPlayVideo } from '@lyearn/shared/components/Players';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';
import ActionList from '@/components/LegacyMediaContainer/ActionList';
import { MediaDisplay, MediaGridProps, MediaSize } from '@/components/LegacyMediaContainer/types';
import { removeTimeFromUrl } from '@/components/MediaPlayer/MediaViewer/utils';
import useLinking from '@/hooks/useLinking';
import { MediaKindEnum } from '@/types/schema';

import { backdropStyle } from './styles';
import { GridRenderProps } from './types';
import { GRID_RENDER_COMPONENT, shouldPlayInline } from './utils';

const autoAdjustClasses = (totalMedias: number) => {
  switch (totalMedias) {
    case 1:
      return 'w-full h-auto xs:h-full';
    default:
      return 'flex-1 me-16';
  }
};

const GridRender: React.FC<GridRenderProps> = (props) => {
  const { kind, progress } = props.media;
  const commonClasses = cn(
    'object-cover overflow-hidden border border-solid border-interactive-secondary rounded-8',
    { 'bg-s-info-light': progress },
  );

  const Component = GRID_RENDER_COMPONENT[kind];

  if (!Component) {
    return null;
  }

  return <Component className={commonClasses} {...props} />;
};

const MediaGrid: React.FC<MediaGridProps> = (props) => {
  const {
    media,
    className,
    size = MediaSize.SMALL,
    onAction,
    actions,
    onClick,
    totalMedias,
    index,
    actionListClassName,
  } = props;
  const { openLink } = useLinking();

  const playInline = shouldPlayInline(media, size);

  const onClickHandler = useCallback(() => {
    const adaptedUrl = removeTimeFromUrl(media.url ?? '');
    const canOpenInMediaPlayer =
      media.kind !== MediaKindEnum.Video || (media.url && canPlayVideo(adaptedUrl));

    if (canOpenInMediaPlayer) {
      !playInline && onClick?.(media);
      return;
    }

    const canNavigateToUrl = media.url && isURL(adaptedUrl);

    if (canNavigateToUrl) {
      openLink(adaptedUrl);
    }
  }, [media, onClick, openLink, playInline]);

  const showBackdropCount = size === MediaSize.ADJUST && index === 2 && totalMedias > 3;

  if (size === MediaSize.ADJUST && index > 2) {
    return null;
  }

  return (
    <Box
      className={cn(
        'group relative mb-8 inline-block bg-cover bg-center',
        size === MediaSize.ADJUST ? autoAdjustClasses(totalMedias) : totalMedias > 1 ? 'me-8' : '',
        className,
      )}
      role={onClick ? 'button' : 'div'}
      onClick={onClickHandler}>
      <GridRender
        index={index}
        media={media}
        playInline={playInline}
        size={size}
        totalMedias={totalMedias}
      />
      <ActionList
        actions={actions}
        className={actionListClassName}
        displayMode={MediaDisplay.GRID}
        media={media}
        onAction={onAction}
      />
      {showBackdropCount ? (
        <Stack
          alignItems="center"
          className={cn('absolute top-0 bottom-0 z-40 rounded-8 end-0 start-0', backdropStyle)}
          justifyContent="center">
          <Typography color="text-text-white" variant="heading-m">
            +{totalMedias - index}
          </Typography>
        </Stack>
      ) : null}
    </Box>
  );
};

export default React.memo(MediaGrid);
